import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Collapse,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  makeStyles,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import classNames from 'classnames';
import { PendingItem } from 'interfaces';
import * as React from 'react';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import { OrderService } from 'services';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    height: 350,
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  cardHeader: {
    background: theme.palette.primary.light,
    padding: theme.spacing(1, 2),
    color: 'white',
  },
  starredHeader: {
    background: theme.palette.secondary.main,
  },
  expand: {
    transform: 'rotate(0deg)',
    padding: 4,
    marginTop: 6,
    marginLeft: 6,
    color: 'white',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
  cutOut: {
    textDecoration: 'line-through',
  },
}));

interface Props {
  order: {
    orderId: number;
    starred: boolean;
    items: PendingItem[];
  };
  toggleStarred?: (orderId: number) => void;
  onCompleteOrder?: (orderId: number) => void;
}

export const LidsOrderCard = React.memo<Props>(
  ({ order, toggleStarred, onCompleteOrder }) => {
    const classes = useStyles();
    const [expand, setExpand] = React.useState(true);

    // Build the initial set of "already lidded" keys from the server-side
    // lidsCheckedCount, using the same per-item-instance key scheme as Packing.
    const getCheckedItems = React.useCallback(
      function getCheckedItems() {
        return order.items.reduce<string[]>((prev, curr) => {
          const lidsCheckedCount = parseInt(curr.lidsCheckedCount, 10);
          const lidsCountUsed = prev.filter(
            (p) => p.indexOf(`${curr.menuItemId}-`) !== -1,
          ).length;

          if (lidsCountUsed < lidsCheckedCount) {
            const itemIndex = order.items.indexOf(curr);
            const key = `${curr.menuItemId}-${itemIndex}`;
            return [...prev, key];
          }

          return prev;
        }, []);
      },
      [order],
    );

    const [checkedItems, setCheckedItems] = React.useState<string[]>(
      getCheckedItems(),
    );

    return (
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <Card>
          <CardHeader
            className={classNames(classes.cardHeader, {
              [classes.starredHeader]: order.starred,
            })}
            title={
              <Typography
                variant="subtitle2"
                style={{ display: 'flex', justifyContent: 'space-between' }}
              >
                <span>
                  {`${order.orderId}`}
                  {' ('}
                  {`${order.items[0].orderNumber ?? 'N/A'}`}
                  {')'}
                </span>
                <span>{`Batch ${order.items[0].batchNumber}`}</span>
                <span>
                  {checkedItems.length} / {order.items.length}
                </span>
              </Typography>
            }
            action={
              <React.Fragment>
                <IconButton
                  className={classes.expand}
                  onClick={() => toggleStarred && toggleStarred(order.orderId)}
                >
                  {order.starred ? <StarIcon /> : <StarBorderIcon />}
                </IconButton>
                <IconButton
                  onClick={() => setExpand(!expand)}
                  className={classNames(classes.expand, {
                    [classes.expandOpen]: expand,
                  })}
                >
                  <ExpandMoreIcon />
                </IconButton>
              </React.Fragment>
            }
          />
          <Collapse in={expand} timeout="auto" unmountOnExit>
            <CardContent>
              <List dense className={classes.root}>
                {order.items.map((item, index) => (
                  <ListItem
                    key={index}
                    style={{ cursor: 'pointer', paddingLeft: 0 }}
                    className={classNames({
                      [classes.cutOut]: checkedItems.includes(
                        `${item.menuItemId}-${index}`,
                      ),
                    })}
                    onClick={() => handleCheckChange(item, index)}
                  >
                    <ListItemText
                      primary={item.menuItem}
                      secondary={item.notes}
                    />
                    <ListItemSecondaryAction style={{ right: 0 }}>
                      <Checkbox
                        edge="end"
                        onChange={() => handleCheckChange(item, index)}
                        checked={checkedItems.includes(
                          `${item.menuItemId}-${index}`,
                        )}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleTickAll}
                disabled={checkedItems.length === order.items.length}
                style={{ marginRight: 8 }}
              >
                Tick All
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  onCompleteOrder && onCompleteOrder(order.orderId)
                }
                disabled={checkedItems.length !== order.items.length}
              >
                Complete
              </Button>
            </CardContent>
          </Collapse>
          <LinearProgress
            variant="determinate"
            value={(checkedItems.length / order.items.length) * 100}
          />
        </Card>
      </Grid>
    );

    function handleCheckChange(item: PendingItem, index: number) {
      const newVal = checkedItems.slice();
      const key = `${item.menuItemId}-${index}`;
      const wasChecked = newVal.includes(key);

      if (wasChecked) {
        setCheckedItems(newVal.filter((i) => i !== key));
      } else {
        setCheckedItems([...newVal, key]);
      }

      // A collapsed combo line stands for several real order items, so
      // record a lid for EVERY component — otherwise only the first
      // component's lid is ever tracked and the counts drift.
      const targetIds =
        item.comboMenuItemIds && item.comboMenuItemIds.length > 0
          ? item.comboMenuItemIds
          : [item.menuItemId];

      targetIds.forEach((menuItemId) => {
        OrderService.lidsCheckOrderItem({
          action: wasChecked ? 'unchecked' : 'checked',
          orderId: item.orderId,
          menuItemId,
        });
      });
    }

    function handleTickAll() {
      // Optimistic: fill the local checked set so the UI updates instantly
      // and the Complete button becomes enabled. Backend wraps the fill in
      // a single transaction; if it fails, the next refresh resyncs state.
      const allKeys = order.items.map(
        (item, idx) => `${item.menuItemId}-${idx}`,
      );
      setCheckedItems(allKeys);
      OrderService.lidsCheckAllForOrder(order.orderId);
    }
  },
);
